import { storeToRefs } from "pinia";
import { filterBasketItemsForDisplay } from "~/utils/cart/filterBasketItemsForDisplay";

/**
 * Orchestration composable for cart initialisation.
 *
 * Call this from `<script setup>` only (it captures Nuxt composables
 * synchronously) and use the returned `initCart` from anywhere — even
 * inside watchers and async event loops.
 */
/**
 * True once the browser has finished resolving the cart — with a basket or
 * without one.
 *
 * Gate on this, never on `cartStore.basket`, when something must not render
 * before the cart is known: `basket` returns to `undefined` in several
 * legitimate cases (completed order, empty cart for the locale, locale switch),
 * so a `v-if="basket"` would unmount mid-flow — and the layout can remount its
 * subtree at any time via `forceRender`.
 *
 * One-way and client-only. `useState` so it's serialized as `false` for SSR:
 * on the server `initCart` returns early (cart ids live in localStorage), so
 * marking it there would let consumers render at hydration, before the real
 * cart load.
 */
export const useCartInitialized = () =>
	useState<boolean>("cart-initialized", () => false);

export const useCartInit = () => {
	const cartStore = useCartStore();
	const route = useRoute();
	const localePath = useLocalePath();
	const nuxtApp = useNuxtApp();
	const { getUser } = storeToRefs(userStore());
	const cartInitialized = useCartInitialized();

	const initCart = (): Promise<void> =>
		nuxtApp
			.runWithContext(async () => {
				// [GF-94] Not on the success page: it waits for the basket to complete.
				const dropStale = !route.path.endsWith("/checkout-success");
				if (getUser.value?.Email) {
					const userResult = await cartStore.initCartUser(dropStale);
					if (userResult.kind === "completed") {
						if (route.path.endsWith("/checkout") && userResult.orderReference) {
							await navigateTo(localePath("/checkout-success"));
							return;
						}
						if (route.path.endsWith("/checkout-success")) {
							cartStore.lastOrderedBasket = filterBasketItemsForDisplay(
								userResult.basket
							);
						}
					}
					return;
				}

				const result = await cartStore.initCart(dropStale);
				if (result.kind !== "completed") return;

				// Completed basket on the checkout route → forward to success
				// with the basket intact; the success page handles cleanup.
				if (route.path.endsWith("/checkout") && result.orderReference) {
					await navigateTo(localePath("/checkout-success"));
					return;
				}

				// Completed basket on the success route → preserve a snapshot
				// for `captureSuccessBasket` to read.
				if (route.path.endsWith("/checkout-success")) {
					cartStore.lastOrderedBasket = filterBasketItemsForDisplay(
						result.basket
					);
				}
			})
			// Whatever the outcome — basket, empty cart or failure — the cart is
			// resolved now, so gated consumers can render. See useCartInitialized.
			.finally(() => {
				if (import.meta.client) cartInitialized.value = true;
			});

	return { initCart, cartInitialized };
};
